import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Loading from "../../../../components/core/blocks/Loading";
import { Checkbox } from "../../../../components/fields/base/Checkbox";
import { SearchInput } from "../../../../components/fields/base/TextField";
import { matchesCommaSearch } from "../../../../components/table/filterFns";
import { HStack, VStack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import { useMarketplaceLabel } from "../../../../utils/marketplaceConstants";
import { type AdsAccount } from "../../../../zodios/apiDefinitions/api/advertisingAccounts";
import { useAmcAdvertisers } from "../../api/useAmcAdvertisers";
import { useAdvertisingAccounts } from "../../../onboarding/amazon/useAdvertisingAccounts";
import Pagination from "../Pagination";

const ACCOUNTS_PER_PAGE = 5;

type AccountGroup = {
  account: AdsAccount;
  /** Every entity ID of the account, which its checkbox selects or clears. */
  entityIds: string[];
  /** The ones the search leaves showing, ordered by country name, then ID. */
  shown: { entityId: string; country: string }[];
};

/**
 * Picks the entity IDs an AMC instance should hold, grouped by ads account. Each account's checkbox
 * selects or clears all of its entity IDs.
 */
export function AmcEntityIdsPicker({
  selectedEntityIds,
  onSelectedEntityIdsChange,
}: {
  selectedEntityIds: ReadonlySet<string>;
  onSelectedEntityIdsChange: (entityIds: ReadonlySet<string>) => void;
}) {
  const { t } = useTranslation(["amc", "common"]);
  const marketplaceLabel = useMarketplaceLabel();
  const { data: accounts, isLoading } = useAdvertisingAccounts();
  const { instance, byId: instanceAdvertisers } = useAmcAdvertisers();
  const instanceLabel =
    !instance ? undefined
    : instance.raw.instanceName ? `${instance.raw.instanceName} (${instance.instance_id})`
    : instance.instance_id;
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  // An account shows all its entity IDs when it matches the search, otherwise only the matching ones.
  const groups = useMemo(
    () =>
      (accounts ?? [])
        .map((account): AccountGroup => {
          const entities = account.entityIds
            .map(({ id, countryCode }) => ({ entityId: id, country: marketplaceLabel(countryCode) }))
            .sort((a, b) => a.country.localeCompare(b.country) || a.entityId.localeCompare(b.entityId));
          const accountMatches = matchesCommaSearch(query, [account.accountName ?? "", account.adsAccountId]);
          return {
            account,
            entityIds: entities.map(({ entityId }) => entityId),
            shown:
              accountMatches ? entities : (
                entities.filter(({ entityId, country }) => matchesCommaSearch(query, [entityId, country]))
              ),
          };
        })
        .filter(
          ({ account, shown }) =>
            shown.length > 0 || matchesCommaSearch(query, [account.accountName ?? "", account.adsAccountId]),
        )
        .sort((a, b) => (a.account.accountName ?? "").localeCompare(b.account.accountName ?? "")),
    [accounts, marketplaceLabel, query],
  );

  const pageGroups = groups.slice((page - 1) * ACCOUNTS_PER_PAGE, page * ACCOUNTS_PER_PAGE);
  const setSelected = (entityIds: string[], isSelected: boolean) => {
    const next = new Set(selectedEntityIds);
    for (const entityId of entityIds) {
      if (isSelected) {
        next.add(entityId);
      } else {
        next.delete(entityId);
      }
    }
    onSelectedEntityIdsChange(next);
  };

  return (
    <VStack gap="medium" alignItems="stretch" paddingBottom="xlarge">
      <HStack justifyContent="space-between">
        <SearchInput
          aria-label={t(($) => $.common.actions["Search"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={query}
          onChange={(value) => {
            setQuery(value);
            setPage(1);
          }}
        />
        <Pagination data={groups} currentPage={page} setCurrentPage={setPage} pageSize={ACCOUNTS_PER_PAGE} />
      </HStack>
      {isLoading ?
        <Loading fullPage={false} height={"200px"} />
      : pageGroups.length === 0 ?
        <p className={css({ textAlign: "center" })}>{t(($) => $.common["No results"])}</p>
      : <VStack gap="large" alignItems="stretch" role="group" aria-label={t(($) => $.amc["Sponsored Ads entity IDs"])}>
          {pageGroups.map((group) => (
            <AccountEntityIds
              key={group.account.adsAccountId}
              group={group}
              selectedEntityIds={selectedEntityIds}
              instanceEntityIds={instanceAdvertisers}
              instanceLabel={instanceLabel}
              setSelected={setSelected}
            />
          ))}
        </VStack>
      }
    </VStack>
  );
}

/** One account's entity IDs as a checkbox group, under a checkbox that selects or clears them all. */
function AccountEntityIds({
  group: { account, entityIds, shown },
  selectedEntityIds,
  instanceEntityIds,
  instanceLabel,
  setSelected,
}: {
  group: AccountGroup;
  selectedEntityIds: ReadonlySet<string>;
  instanceEntityIds: ReadonlyMap<string, unknown>;
  /** The instance's name and ID. */
  instanceLabel: string | undefined;
  setSelected: (entityIds: string[], isSelected: boolean) => void;
}) {
  const { t } = useTranslation(["amc"]);
  const headingId = useId();
  const selectedCount = entityIds.filter((entityId) => selectedEntityIds.has(entityId)).length;

  return (
    <div role="group" aria-labelledby={headingId} className={css({ "& label": { fontSize: "medium" } })}>
      <HStack gap="medium" id={headingId} className={css({ "& label": { fontWeight: "600" } })}>
        <Checkbox
          label={account.accountName ?? account.adsAccountId}
          value={
            selectedCount === 0 ? false
            : selectedCount === entityIds.length ?
              true
            : "indeterminate"
          }
          isDisabled={entityIds.length === 0}
          onChange={(isSelected) => setSelected(entityIds, isSelected)}
        />
        {instanceLabel && entityIds.some((entityId) => instanceEntityIds.has(entityId)) && (
          <span className={css({ fontSize: "small", color: "grey" })}>
            {`${t(($) => $.amc["AMC Instance"])}: ${instanceLabel}`}
          </span>
        )}
        {entityIds.length === 0 && (
          <span className={css({ color: "grey" })}>{t(($) => $.amc["This account has no entity IDs."])}</span>
        )}
      </HStack>
      <div
        className={css({
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          columnGap: "large",
          rowGap: "xsmall",
          paddingTop: "small",
          paddingLeft: "large",
        })}
      >
        {shown.map(({ entityId, country }) => (
          <div key={entityId}>
            <Checkbox
              size="small"
              label={`${country}: ${entityId}`}
              value={selectedEntityIds.has(entityId)}
              onChange={(isSelected) => setSelected([entityId], isSelected)}
            />
            {instanceEntityIds.has(entityId) && (
              // Indented by the small checkbox (12px plus its border) and the gap, to sit under the label.
              <div className={css({ paddingLeft: "calc(14px + {spacing.small})", fontSize: "small", color: "grey" })}>
                {t(($) => $.amc["In AMC instance"])}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
